/**
 * OverviewBlock — one block of the patient Overview hub (patient pages switch, `?x=patient-pages`; prototype
 * commit b8afdd2). A soft icon, the block title, and a quiet "Label ↗" link on the right that opens the block's
 * full page; the headline data sits under it. Each block links to its own page, like the dashboard's blocks.
 * Look lives in the bridge under the "patient pages" heading, keyed on `data-slot`.
 */
import type { ReactNode } from 'react';
import { Icon } from '@scripsteam/scrips-signal-ds';

export interface OverviewBlockProps {
  title: string;
  icon: ReactNode;
  /** The link's label ("Appointments", "Full chart"…); the arrow is drawn after it. */
  linkLabel: string;
  onOpen: () => void;
  /** Spans both columns (the Visits block). */
  wide?: boolean;
  /** A muted tint behind the card (prototype commit 22ad00f; Antonin, 15 Oct: "keep the cards"). None = the white card. */
  tint?: 'amber' | 'green' | 'blue';
  children: ReactNode;
}

export function OverviewBlock({ title, icon, linkLabel, onOpen, wide = false, tint, children }: OverviewBlockProps) {
  return (
    <section data-slot="overview-block" data-wide={wide ? 'true' : undefined} data-tint={tint} aria-label={title}>
      <header data-slot="overview-block-head">
        <span data-slot="overview-block-icon" aria-hidden="true">
          {icon}
        </span>
        <h2 data-slot="overview-block-title">{title}</h2>
        <button type="button" data-slot="overview-block-link" onClick={onOpen}>
          {linkLabel}
          {/* The design's link glyph (PaOverviewTab `.link`): the DS external arrow, 16. */}
          <Icon name="external" size={16} color="currentColor" aria-hidden />
        </button>
      </header>
      <div data-slot="overview-block-body">{children}</div>
    </section>
  );
}

/** A block's headline: the count as the data role, what it counts, then up to three names. */
export function OverviewHeadline({
  count,
  noun,
  items,
  emptyText,
  countText,
}: {
  count: number;
  noun: string;
  items: readonly string[];
  emptyText: string;
  /** Shown instead of the number when the list is only the first page ("20+"). */
  countText?: string;
}) {
  if (count === 0) return <p data-slot="overview-empty">{emptyText}</p>;
  return (
    <>
      <p data-slot="overview-headline">
        <span data-slot="overview-count">{countText ?? count}</span> {noun}
      </p>
      {items.length > 0 ? (
        <ul data-slot="overview-items">
          {items.map((it) => (
            <li key={it}>{it}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}
